import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Insight } from '../../../../common/hooks/use-insights-api.hook';
import { findingText, severityStyle } from '../insight-presentation';

/**
 * One finding, one line.
 *
 * The queue is usually a dozen things and the strip of cards showed four of
 * them, sideways, behind a scroll most people never try. A row costs a line,
 * so the whole queue is visible and can be read down in one pass — and the
 * one that actually matters gets a card of its own above.
 *
 * The title alone, deliberately. A finding's reasoning is three or four
 * sentences and belongs where there is room for it; what a scan needs is
 * whether this is the one to open.
 */
export const InsightRow: React.FC<{ finding: Insight; onOpen: () => void }> = ({ finding, onOpen }) => {
    const { t } = useTranslation();
    const style = severityStyle(finding.severity, finding.sentiment);

    return (
        <button
            type="button"
            onClick={onOpen}
            className="group/row flex w-full items-center gap-3 border-b py-2.5 text-start last:border-b-0 hover:bg-accent/40"
        >
            <span className={cn('size-1.5 shrink-0 rounded-full', style.dot)} aria-hidden />
            <span className="min-w-0 flex-1 truncate text-sm">
                {findingText(t, finding.title, { key: finding.i18n?.title, params: finding.i18n?.params })}
            </span>

            {/*
              The word only when it is critical.
             
              Printing the severity on every line put "Needs attention" down the
              whole column, which is a lot of type to say what the dot already
              says — and it made the one row that IS critical look like the
              rest. Screen readers still get every row's severity, since a
              colour is nothing to them.
            */}
            {finding.severity === 'critical' ? (
                <span className="shrink-0 text-xs text-destructive">{t(style.labelKey)}</span>
            ) : (
                <span className="sr-only">{t(style.labelKey)}</span>
            )}
            <ChevronRight className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100" />
        </button>
    );
};
